import { inject, Injectable, makeStateKey, TransferState } from '@angular/core'
import { Database } from 'src/server/database/Database'
import { ED_INJECT_DATABASE, ED_INJECT_LOCALE_ID, ED_INJECT_EXPRESS_RESPONSE } from 'src/server/server.data'
import { RuntimeService } from 'src/app/service/runtime.service'
import { Environment } from 'src/environments/environment'
import { MetaService } from 'src/app/service/meta/meta.service'
import punycode from 'punycode'
import { md5 } from 'js-md5'

export type SalePageType =
  Redirector4SaleConfig
  | RedirectorPaynowConfig
  | RedirectorPaynowSoldConfig
  | RedirectorErrorSetupConfig
  | RedirectorDomainCatchConfig
  | RedirectorErrorArgpConfig

export type Paynow4SaleType = Redirector4SaleConfig | RedirectorPaynowConfig

export const ED_STORE_CONTEXT = makeStateKey<PageContext<SalePageType>>('ED_STORE_REDIRECTOR')

export type PageContext<SaleType extends SalePageType> =
  SaleType extends RedirectorPaynowSoldConfig | RedirectorErrorSetupConfig | RedirectorErrorArgpConfig
    ? {
      system_page: true, // sold, error, ...
      config: SaleType
      i18n: {
        locale: string
        primary: string
      }
      links: {
        terms: string
        promo_index: string
        promo_catcher: string
        promo_sell_domains: string
        privacy: string
        provider: string
        marketplace: string
        login: string
        register: string
        app_catcher: string
      }
    }
    : {
      system_page: false,
      user: {
        paynow: {
          price_type: 'brutto' | 'netto' | 'neutral'
          public_phone?: string
        }
      }
      config: SaleType
      i18n: {
        locale: string
        primary: string
      }
      offer: {
        reserved: boolean
        external: boolean
        connected: boolean
        meta_tags: JSON | null
      }
      links: {
        terms: string
        promo_index: string
        promo_catcher: string
        promo_sell_domains: string
        login: string
        register: string
        app_catcher: string
        privacy: string
        provider: string
        contact_form: string
        marketplace: string
        checkout: string
      }
    }

export interface Redirector4SaleConfig {
  name: string
  type: '4sale'
  price?: number
  template: 'smart' | 'expert' | 'default'
  template_data: {
    image: null | string
    'price-suggest': boolean
    'theme-color': 'blue' | 'green' | 'yellow' | 'orange' | 'red' | 'purple' | 'gold' | 'black'
    'individual-content--headline': string
    'individual-content--text': string
    qualified_leads: boolean
  }
}

export interface RedirectorPaynowConfig {
  name: string
  type: 'paynow'
  price: number
  template: 'sicherheit' | 'vertrieb'
  template_data: {
    image: null
    'price-suggest': boolean
    'theme-color': 'blue' | 'green' | 'yellow' | 'orange' | 'red' | 'purple' | 'gold' | 'black'
    'individual-content--text': string
    'individual-content--headline': string
  }
}

export interface RedirectorPaynowSoldConfig {
  name: string
  type: 'paynow'
  template: 'sold'
}

type ErrorVerificationKey = false | { type: 'inline', key: string } | { type: 'account-ref' }
export interface RedirectorErrorSetupConfig {
  name: string
  type: 'error'
  template: 'setup'
  verification_key: ErrorVerificationKey
  state: 'inactive' | 'unverified'
}

export interface RedirectorErrorArgpConfig {
  name: string
  type: 'error'
  template: 'argp'
}

export interface RedirectorDomainCatchConfig {
  name: string
  type: 'landing'
  template: 'recently-caught'
}

@Injectable()
export class RedirectorService {
  protected context!: PageContext<SalePageType>
  protected database?: Database
  protected domain?: string
  protected host?: string
  protected locale = inject(ED_INJECT_LOCALE_ID)
  protected response = inject(ED_INJECT_EXPRESS_RESPONSE, { optional: true })

  constructor (
    protected runtimeService: RuntimeService,
    protected transferState: TransferState,
    protected metaService: MetaService,
  ) {
    if (this.runtimeService.isServer()) {
      this.database = inject(ED_INJECT_DATABASE)
      this.domain = this.metaService.getEncodedDomain()
      this.host = this.metaService.getHost()
    }
  }

  getContext () {
    return this.context
  }

  async boot () {
    if (this.runtimeService.isServer()) {
      this.context = await this.fetchRedirectorConfig()
      this.transferState.set(ED_STORE_CONTEXT, this.context)

      const asciiDomain = punycode.toASCII(this.domain!)
      if (asciiDomain !== this.host && !this.context.system_page && this.context.offer.connected) {
        if (this.response) {
          this.response.set("location", `https://${asciiDomain}`)
          this.response.status(301)
        }
      }
    } else {
      // @ts-ignore
      this.context = this.transferState.get(ED_STORE_CONTEXT, undefined)
    }

    this.metaService.setDomain(this.context)
  }

  protected async fetchRedirectorConfig () {
    const sql = `
      SELECT id,
             name,
             type,
             price,
             status,
             template,
             template_data,
             seller_id,
             domain_id,
             redirect_status,
             main_language,
             meta_tags
      FROM domain_offers
      WHERE name = ?
        AND type NOT IN ('inactive', 'not_configured')
        AND status IN ('verified', 'hold')
        AND deleted_at IS NULL
      LIMIT 1;
    `

    let domainOffer = await this.database!.first(sql, [this.domain])

    if (!domainOffer) {
      const domain = await this.database!.first(
        'SELECT status, redirector_settings FROM domains WHERE name = ? AND deleted_at IS NULL LIMIT 1;',
        [this.domain],
      ) as any as { status: string, redirector_settings: any }

      if (domain) {
        domain['redirector_settings'] = JSON.parse(domain['redirector_settings'])
      }

      if (
        domain?.redirector_settings.type == 'landing'
        && domain?.redirector_settings.method == 'catch'
      ) {
        domainOffer = {
          name: this.domain,
          type: 'landing',
          template: 'recently-caught',
        } as RedirectorDomainCatchConfig
      } else {
        const transaction = await this.database!.first(
          `SELECT payment_status FROM paynow_transactions WHERE name = ? ORDER BY created_at DESC LIMIT 1`,
          [this.domain],
        ) as { payment_status: string }

        if (transaction?.payment_status === 'success') {
          domainOffer = {
            name: this.domain,
            type: 'paynow',
            template: 'sold',
          } as RedirectorPaynowSoldConfig
        } else {
          let verificationKey: ErrorVerificationKey = false
          const offerRows = await this.database!.get(`
          SELECT seller_id, status
          FROM domain_offers
          WHERE name = ?
            AND deleted_at IS NULL
          LIMIT 2;
        `, [this.domain])
          const offerRow = offerRows[0]

          if (
            offerRows.length
            && ['inactive', 'unverified'].includes(offerRow?.status)
          ) {
            if (offerRows.length > 1) {
              verificationKey = {
                type: 'account-ref',
              }
            } else {
              const sellingUser = await this.database!.first(`
              SELECT id
              FROM users
              WHERE id = ?
              LIMIT 2;
            `, [offerRow['seller_id']])

              verificationKey = {
                type: 'inline',
                key: md5(sellingUser['id'].toString()).substring(0, 6)
              }
            }
          }

          domainOffer = {
            name: this.domain,
            type: 'error',
            template: 'setup',
            verification_key: verificationKey,
            state: (offerRows?.[0]?.status === 'inactive')
              ? 'inactive'
              : 'unverified',
          } as RedirectorErrorSetupConfig
        }
      }

      return await this.transformContext({
        system_page: true,
        links: {
          terms: Environment.url.promo + '/static/pdf/agb.pdf',
          promo_index: Environment.url.promo,
          promo_catcher: Environment.url.promo + '/de-catcher',
          promo_sell_domains: Environment.url.promo + '/domains-verkaufen',
          login: Environment.url.app + '/login',
          register: Environment.url.app + '/register',
          provider: Environment.url.promo + '/provider',
          app_catcher: Environment.url.app + '/catcher/droplist',
          privacy: Environment.url.promo + '/datenschutz',
          marketplace: Environment.url.promo + '/marktplatz',
        },
        i18n: {
          locale: this.locale,
          primary: this.getPrimaryLanguage(this.domain as string, domainOffer),
        },
        config: domainOffer,
      })
    } else if (typeof domainOffer?.['template_data'] === 'string') {
      domainOffer['template_data'] = JSON.parse(domainOffer['template_data'])
    }

    const domain = await this.database!.first(
      'SELECT status FROM domains WHERE name = ? AND deleted_at IS NULL LIMIT 1;',
      [this.domain],
    ) as any as { status: string }
    if (domain?.status === 'auto_renew_grace_period') {
      return await this.transformContext({
        system_page: true,
        links: {
          terms: Environment.url.promo + '/static/pdf/agb.pdf',
          promo_index: Environment.url.promo,
          promo_catcher: Environment.url.promo + '/de-catcher',
          promo_sell_domains: Environment.url.promo + '/domains-verkaufen',
          login: Environment.url.app + '/login',
          register: Environment.url.app + '/register',
          provider: Environment.url.promo + '/provider',
          app_catcher: Environment.url.app + '/catcher/droplist',
          privacy: Environment.url.promo + '/datenschutz',
          marketplace: Environment.url.promo + '/marktplatz',
        },
        i18n: {
          locale: this.locale,
          primary: this.getPrimaryLanguage(this.domain as string, domainOffer),
        },
        config: {
          name: this.domain,
          type: 'error',
          template: 'argp',
        } as RedirectorErrorArgpConfig,
      })
    }

    const external = domainOffer['domain_id'] === null
    let reserved: boolean
    let connected: boolean
    if (external) {
      reserved = domainOffer['status'] === 'hold'
        || await this.database!.exists(
          `SELECT * FROM paynow_transactions WHERE offer_id = ? AND payment_status = 'success'  AND transfer_status = 'pending' LIMIT 1`,
          [domainOffer['id']],
        )
      connected = domainOffer['redirect_status'] == 1
    } else {
      const domain = await this.database!.first(
        'SELECT status, redirector_settings FROM domains WHERE id = ? LIMIT 1;',
        [domainOffer['domain_id']],
      ) as any as { status: string, redirector_settings: any }

      if (typeof domain?.['redirector_settings'] === 'string') {
        domain['redirector_settings'] = JSON.parse(domain['redirector_settings'])
      }

      if (domain?.status === 'hold' || domainOffer['status'] === 'hold') {
        reserved = true;
      } else {
        const transaction = await this.database!.first(
          'SELECT payment_status FROM paynow_transactions WHERE offer_id = ? ORDER BY created_at DESC LIMIT 1;',
          [domainOffer['id']],
        ) as any as { payment_status: string }
        reserved = transaction?.payment_status === 'hold'
      }

      connected = domain?.redirector_settings?.type === 'landing' && domain?.redirector_settings?.method === 'redirect_sale_page'
    }

    delete(domainOffer['id']);
    delete(domainOffer['domain_id']);
    delete(domainOffer['status']);

    const user = await this.database!.first(
      'SELECT paynow_settings FROM users WHERE id = ? LIMIT 1;',
      [domainOffer['seller_id']],
    ) as { paynow_settings: any }

    if (user.paynow_settings) {
      user.paynow_settings = JSON.parse(user.paynow_settings)
    }

    return await this.transformContext({
      system_page: false,
      user: {
        paynow: {
          price_type: user.paynow_settings.price_type ?? 'brutto',
          public_phone: user.paynow_settings.public_phone ?? false,
        },
      },
      links: {
        terms: Environment.url.promo + '/static/pdf/agb.pdf',
        promo_index: Environment.url.promo,
        promo_catcher: Environment.url.promo + '/de-catcher',
        promo_sell_domains: Environment.url.promo + '/domains-verkaufen',
        login: Environment.url.app + '/login',
        register: Environment.url.app + '/register',
        provider: Environment.url.promo + '/provider',
        app_catcher: Environment.url.app + '/catcher/droplist',
        privacy: Environment.url.promo + '/datenschutz',
        contact_form: [Environment.url.checkout, this.locale, domainOffer.name, 'contact?theme=' + domainOffer.template_data['theme-color']].join('/'),
        marketplace: Environment.url.promo + '/marktplatz',
        checkout: [Environment.url.checkout, this.locale, domainOffer.name].join('/'),
      },
      offer: {
        reserved,
        external,
        connected,
        meta_tags: domainOffer['meta_tags'],
      },
      i18n: {
        locale: this.locale,
        primary: this.getPrimaryLanguage(this.domain as string, domainOffer),
      },
      config: domainOffer,
    })
  }

  protected async transformContext (context: PageContext<SalePageType>) {
    const { ReplacerTools } = await import('src/tools/replacer.tools')

    if (

      context.config.type !== 'error'
      && context.config.type !== 'landing'
      && !(context.config?.type === 'paynow' && context.config.template === 'sold')
    ) {
      if (context.config?.template_data?.['individual-content--headline']) {
        context.config.template_data['individual-content--headline'] = ReplacerTools.applyReplacers(
          context.config.template_data['individual-content--headline'],
          context.config.name,
        )
      }

      if (context.config?.template_data?.['individual-content--text']) {
        const { MarkdownTools } = await import('src/tools/markdown.tools')

        let text = context.config.template_data['individual-content--text']
        text = ReplacerTools.applyReplacers(text, context.config.name)
        text = MarkdownTools.render(text)

        context.config.template_data['individual-content--text'] = text
      }

      context.config.template_data['price-suggest']
        = ['true', '1', true].includes(context.config.template_data['price-suggest'] as any)
    }

    if (context.config.type === '4sale') {
      context.config.template_data['qualified_leads']
        = ['true', '1', true].includes(context.config.template_data['qualified_leads'] as any)
    }

    return context
  }

  protected transformUserImprintUrl (link: string) {
    if (!link || link.startsWith('http') || link.startsWith('//')) {
      return link
    }

    return `//${link}`
  }

  private getPrimaryLanguage (domain: string, offer?: any) {
    if (offer['main_language']) {
      return offer['main_language']
    }

    return domain.includes('.de')
      ? 'de'
      : 'en'
  }
}
